// The waves of the tower defence: who comes down the road and when, one idea per wave and harder
// each time, and from the sixth wave the ships offshore that launch at the Command Center over the
// top of it all. Twenty are made by hand; past them, waves are generated from a pool that opens up
// as the number climbs, each a little bigger than the last, so a game goes on until the Command
// Center falls. Everything is deterministic in the wave number. At DEFCON 1 (`nuclear`) the enemy
// goes nuclear from the twentieth wave: a submarine offshore fires ICBMs, and the glide vehicles
// carry nuclear warheads.
import { DEFAULT_CONFIG } from './data.ts';
import { TICKS_PER_SECOND } from './fixed.ts';
import { Rng } from './rng.ts';

export interface TdGroup {
  unit: string;
  count: number;
  /** Ticks between one and the next. */
  gap: number;
  /** Ticks after the wave starts that the first one comes. */
  at: number;
}

/** A ship of the wave's fleet: it holds station offshore and launches `count` of `unit` at the Command Center. */
export interface TdShip {
  ship: string;
  unit: string;
  count: number;
  /** Ticks between one launch and the next. */
  gap: number;
  /** Ticks after the wave starts that it sails in; its first launch comes once it is on station. */
  at: number;
}

export interface TdWave {
  n: number;
  name: string;
  /** One line said as it comes in. */
  briefing: string;
  groups: TdGroup[];
  /** The ships offshore this wave, and what they launch. */
  ships: TdShip[];
  boss: boolean;
}

const S = TICKS_PER_SECOND;
const g = (unit: string, count: number, gapSec: number, atSec = 0): TdGroup => ({ unit, count, gap: Math.round(gapSec * S), at: Math.round(atSec * S) });
const ship = (ship: string, unit: string, count: number, gapSec: number, atSec = 0): TdShip => ({ ship, unit, count, gap: Math.round(gapSec * S), at: Math.round(atSec * S) });

interface Spec { name: string; briefing: string; groups: TdGroup[]; ships?: TdShip[] }

const SPECS: Spec[] = [
  { name: 'Knock Knock', briefing: 'A dozen riflemen on the road. A machine gun by the first bend is all it takes.', groups: [g('rifleman', 12, 0.8)] },
  { name: 'Militia', briefing: 'Conscripts, badly armed and plentiful, with riflemen behind.', groups: [g('conscript', 16, 0.5), g('rifleman', 8, 0.8, 9)] },
  { name: 'Double Time', briefing: 'The same men, twice as close together. One gun will not keep up.', groups: [g('rifleman', 20, 0.45)] },
  { name: 'Eyes Up', briefing: 'Recon drones first: unarmed, but your machine guns cannot reach them. Then the men.', groups: [g('scout_drone', 3, 1.2), g('rifleman', 16, 0.6, 4)] },
  { name: 'Marksmen', briefing: 'Marksmen shoot the crews of your nests and posts as they pass. Keep the guns in range of each other.', groups: [g('marksman', 8, 1.2), g('rifleman', 12, 0.5, 5)] },
  { name: 'Buzz', briefing: 'FPV drones: tiny, fast and cheap, and nothing on the ground can hit them. Build a Drone Interceptor. And a ship offshore is launching Shaheds at the Command Center: nothing you build reaches it, so stop what it sends.', groups: [g('fpv_drone', 10, 0.6), g('rifleman', 10, 0.6, 4)], ships: [ship('drone_ship', 'shahed', 3, 5, 2)] },
  { name: 'Treads', briefing: 'Two tanks. Machine guns scratch the paint; you want an ATGM Post or an anti-tank mine.', groups: [g('tank', 2, 3), g('rifleman', 12, 0.5, 2)], ships: [ship('drone_ship', 'shahed', 2, 6, 4)] },
  { name: 'Machine Guns', briefing: 'Machine gun teams outrange a rifle and go for your guns. Conscripts soak up the fire for them.', groups: [g('heavy_gunner', 6, 1.5), g('conscript', 20, 0.4, 2)], ships: [ship('drone_ship', 'shahed', 3, 5, 3)] },
  { name: 'Loiterers', briefing: 'Loitering munitions pick a tower each and dive on it. Air defence, or lose a gun a drone. The drone ship is back, with more.', groups: [g('attack_drone', 8, 1), g('marksman', 6, 1, 5)], ships: [ship('drone_ship', 'shahed', 4, 4, 3)] },
  { name: "Ironside's Column", briefing: 'Four tanks in line, anti-tank teams behind them, and a crowd of riflemen in the dust. Ironside does not do subtle, and two car bombs come flat out behind it all.', groups: [g('tank', 4, 2.5), g('at_team', 3, 1.5, 4), g('rifleman', 20, 0.4, 6), g('fpv_drone', 8, 0.5, 12), g('car_bomb', 2, 5, 16)], ships: [ship('drone_ship', 'shahed', 4, 4, 6)] },
  { name: 'Rotor Wash', briefing: 'Attack helicopters hover over your guns and rake them. Only air defence reaches them.', groups: [g('attack_heli', 3, 3), g('rifleman', 16, 0.5, 2)], ships: [ship('drone_ship', 'shahed', 3, 5, 2)] },
  { name: 'Shaheds at Dawn', briefing: 'Slow, loud, and carrying fifty kilos each, straight at the Command Center over the road and in off the sea from two ships at once. The machine gun teams come in under them.', groups: [g('shahed', 8, 1.5), g('heavy_gunner', 6, 1.2, 4)], ships: [ship('drone_ship', 'shahed', 4, 4, 0), ship('drone_ship', 'shahed', 4, 4, 8)] },
  { name: 'Armour Push', briefing: 'Five tanks and the teams to cover them, and car bombs racing up the road between them. Anti-tank mines pay for themselves here.', groups: [g('tank', 5, 2), g('at_team', 4, 1.5, 3), g('car_bomb', 3, 4, 5)], ships: [ship('drone_ship', 'shahed', 4, 4, 2)] },
  { name: 'Swarm', briefing: 'Two dozen FPVs, then loitering munitions behind them. A single interceptor drowns.', groups: [g('fpv_drone', 24, 0.3), g('attack_drone', 8, 1, 4)], ships: [ship('drone_ship', 'shahed', 4, 4, 6)] },
  { name: 'Steel Rain', briefing: 'Cruise missiles straight at the Command Center at nine tiles a second, and a frigate offshore firing Kalibrs at it too. Iron Dome, or a C-RAM, or hope.', groups: [g('cruise_missile', 5, 2.5), g('rifleman', 20, 0.4, 2), g('tank', 2, 3, 10)], ships: [ship('destroyer', 'kalibr', 3, 6, 4)] },
  { name: 'Air Raid', briefing: 'Helicopters and Shaheds together, with recon drones marking your guns for them, the drone ship launching under cover of it, and kamikaze fighters diving straight for the Command Center.', groups: [g('scout_drone', 2, 1), g('attack_heli', 5, 2, 1), g('shahed', 8, 1, 3), g('kamikaze', 3, 3, 6)], ships: [ship('drone_ship', 'shahed', 5, 3.5, 2)] },
  { name: 'Combined Arms', briefing: 'Everything that walks or rolls, in one column, car bombs among it. The ground defence is tested whole.', groups: [g('tank', 4, 2), g('heavy_gunner', 8, 1, 2), g('marksman', 8, 1, 4), g('at_team', 4, 1.5, 8), g('car_bomb', 4, 3, 6)], ships: [ship('destroyer', 'kalibr', 3, 6, 5)] },
  { name: 'Ballistic', briefing: 'Ballistic missiles fall from above, on the Command Center or on your guns: five seconds for a Patriot to stop each one. The frigate fires under them.', groups: [g('ballistic', 3, 5, 2), g('cruise_missile', 4, 2, 4), g('tank', 3, 2.5, 8)], ships: [ship('destroyer', 'kalibr', 4, 5, 6)] },
  { name: 'Everything Flies', briefing: 'Nothing on the ground. Everything in the air, from every size, a squadron of kamikazes, and two drone ships feeding it.', groups: [g('attack_heli', 6, 2), g('shahed', 10, 1, 2), g('fpv_drone', 16, 0.3, 4), g('attack_drone', 8, 0.8, 8), g('kamikaze', 6, 2, 10)], ships: [ship('drone_ship', 'shahed', 5, 3, 0), ship('drone_ship', 'shahed', 5, 3, 10)] },
  { name: 'General Thrash', briefing: 'Everything. The General has read the whole manual and brought all of it, the whole fleet offshore, and a glide vehicle to finish.', groups: [g('tank', 8, 1.5), g('rifleman', 30, 0.3, 3), g('attack_heli', 4, 2, 6), g('ballistic', 4, 4, 10), g('cruise_missile', 6, 1.5, 15), g('hgv', 1, 1, 22)], ships: [ship('destroyer', 'kalibr', 4, 5, 2), ship('destroyer', 'kalibr', 4, 5, 12), ship('drone_ship', 'shahed', 6, 3, 5)] },
];

export const HAND_MADE = SPECS.length;

/**
 * The fleet past the hand-made waves: it grows with the wave number. Frigates from the fifteenth
 * wave, drone ships from the sixth, each with a few more rounds in its launchers every few waves;
 * the kalibrs are capped, as the warheads are, because each one through is a twentieth of the
 * Command Center and they come in seconds.
 */
export function fleetFor(n: number, nuclear = false): TdShip[] {
  // DEFCON 1: the submarine surfaces from the tenth wave, every third wave, and for the General's own
  const sub = nuclear && n <= HAND_MADE && (n === HAND_MADE || (n >= SUB_FROM && (n - SUB_FROM) % 3 === 0));
  if (n <= HAND_MADE) return [...(SPECS[Math.max(0, n - 1)]!.ships ?? []), ...(sub ? [SUBMARINE(1)] : [])];
  const k = n - HAND_MADE;
  const ships: TdShip[] = [];
  // DEFCON 1: a submarine lies offshore with a nuclear missile or two, more every eight waves
  if (nuclear && n >= NUCLEAR_FROM) ships.push(SUBMARINE(Math.min(3, 1 + Math.floor((n - NUCLEAR_FROM) / 8))));
  const frigates = 1 + Math.floor(k / 6);
  const kalibrs = Math.min(8, 4 + Math.floor(k / 4));
  for (let i = 0; i < frigates; i++) ships.push(ship('destroyer', 'kalibr', kalibrs, 5, i * 8));
  const droneShips = 1 + Math.floor(k / 8);
  const shaheds = Math.min(10, 5 + Math.floor(k / 3));
  for (let i = 0; i < droneShips; i++) ships.push(ship('drone_ship', 'shahed', shaheds, 3, 3 + i * 10));
  return ships;
}

/** The wave the nuclear weapons come from at DEFCON 1, past the boss on the twentieth. */
export const NUCLEAR_FROM = HAND_MADE + 1;
/** At DEFCON 1 the submarine's first ICBM comes with this wave. */
export const SUB_FROM = 10;
/** The submarine of DEFCON 1: it sails in ten seconds into the wave and fires a nuclear ICBM every twenty-five. */
const SUBMARINE = (count: number): TdShip => ship('submarine', 'icbm', count, 25, 10);

/** A generated wave stacks this many threats, drawn from what the wave number has opened up. */
const POOL: { from: number; unit: string; weight: number }[] = [
  { from: 1, unit: 'rifleman', weight: 10 }, { from: 1, unit: 'conscript', weight: 6 }, { from: 5, unit: 'marksman', weight: 5 },
  { from: 6, unit: 'fpv_drone', weight: 6 }, { from: 7, unit: 'tank', weight: 7 }, { from: 8, unit: 'heavy_gunner', weight: 5 },
  { from: 9, unit: 'attack_drone', weight: 5 }, { from: 10, unit: 'at_team', weight: 4 }, { from: 11, unit: 'attack_heli', weight: 5 },
  { from: 10, unit: 'car_bomb', weight: 4 }, { from: 12, unit: 'shahed', weight: 5 }, { from: 16, unit: 'kamikaze', weight: 4 }, { from: 15, unit: 'cruise_missile', weight: 4 }, { from: 18, unit: 'ballistic', weight: 3 },
  { from: 20, unit: 'hgv', weight: 2 },
];
/** What DEFCON 1 adds to the pool. */
const NUCLEAR_POOL: typeof POOL = [{ from: NUCLEAR_FROM, unit: 'hgv_nuclear', weight: 2 }];

/** A wave's weight, in the housing the raids used as a unit's size. */
export function waveSize(w: TdWave): number {
  return w.groups.reduce((n, x) => n + (DEFAULT_CONFIG.units[x.unit]?.housing ?? 1) * x.count, 0);
}

/** n grown by `pct` percent `times` over, in integers, so it compounds the same on every engine. */
function grow(n: number, pct: number, times: number): number {
  let p = 100;
  for (let k = 0; k < times; k++) p = Math.floor((p * (100 + pct)) / 100);
  return Math.floor((n * p + 99) / 100);
}

/** How much bigger each generated wave is than the one before, in percent. */
export const GROWTH_PCT = 8;

/**
 * Past the hand-made waves the enemy also gets tougher, this many percent of hit points a wave:
 * numbers alone are matched by the cash they pay, so a game would never end; armour is not.
 */
export const ARMOUR_PCT_PER_WAVE = 5;
export function armourPct(n: number): number {
  return n <= HAND_MADE ? 100 : grow(100, ARMOUR_PCT_PER_WAVE, n - HAND_MADE);
}

/** The wave with this number: hand-made up to HAND_MADE, generated past it; `nuclear` is DEFCON 1. */
export function tdWave(n: number, nuclear = false): TdWave {
  if (n <= HAND_MADE) {
    const s = SPECS[Math.max(0, n - 1)]!;
    // DEFCON 1's twentieth: the General's glide vehicle is nuclear, and the submarine surfaces for the first time
    const groups = nuclear && n === HAND_MADE ? s.groups.map(x => (x.unit === 'hgv' ? { ...x, unit: 'hgv_nuclear' } : x)) : s.groups;
    const briefing = nuclear && n === HAND_MADE ? `${s.briefing} And a submarine offshore: nuclear launch expected.` : s.briefing;
    return { n, name: s.name, briefing, groups, ships: fleetFor(n, nuclear), boss: n % 10 === 0 };
  }
  const rng = new Rng(0x7d5 + n);
  const last = tdWave(HAND_MADE);
  const target = grow(waveSize(last), GROWTH_PCT, n - HAND_MADE);
  const open = [...POOL, ...(nuclear ? NUCLEAR_POOL : [])].filter(p => p.from <= n);
  const boss = n % 10 === 0;
  // three or four kinds, weighted; a boss wave leans on the heaviest it can field
  const kinds = boss ? 4 : 3 + rng.int(2);
  const picked: string[] = [];
  for (let k = 0; k < kinds && picked.length < open.length; k++) {
    const left = open.filter(p => !picked.includes(p.unit));
    const total = left.reduce((t, p) => t + p.weight, 0);
    let roll = rng.int(total);
    for (const p of left) { roll -= p.weight; if (roll < 0) { picked.push(p.unit); break; } }
  }
  if (boss && !picked.includes('tank')) picked[0] = 'tank';
  // the size is split over the kinds by their weight, then turned into counts by each unit's housing
  const share = picked.map(u => open.find(p => p.unit === u)!.weight);
  const shareTotal = share.reduce((a, b) => a + b, 0);
  const groups: TdGroup[] = picked.map((unit, i) => {
    const housing = Math.max(1, DEFAULT_CONFIG.units[unit]?.housing ?? 1);
    const budget = Math.floor((target * share[i]!) / shareTotal);
    // the warheads are capped: each one through is a tenth of the Command Center, and they come in seconds
    const cap = unit === 'hgv_nuclear' ? 1 + Math.floor((n - NUCLEAR_FROM) / 10) : unit === 'hgv' ? 1 + Math.floor((n - 20) / 8) : unit === 'ballistic' ? 2 + Math.floor((n - 18) / 6) : Infinity;
    const count = Math.min(cap, Math.max(1, Math.floor(budget / housing)));
    const gapSec = housing >= 6 ? 2 : housing >= 3 ? 1.2 : housing >= 2 ? 0.8 : 0.35;
    return g(unit, count, gapSec, i * (3 + rng.int(4)));
  });
  const names = picked.map(u => DEFAULT_CONFIG.units[u]?.name ?? u);
  const ships = fleetFor(n, nuclear);
  const sub = ships.some(sh => sh.ship === 'submarine');
  return {
    n, boss,
    name: boss ? `Boss ${n / 10}: ${names[0]} column` : `Wave ${n}`,
    briefing: `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}, ${GROWTH_PCT} percent more than last time, and ${ships.length} ${ships.length === 1 ? 'ship' : 'ships'} offshore${sub ? ', one of them a missile submarine' : ''}.`,
    groups, ships,
  };
}

/** Every unit that can come down the road or in off the sea, for the guide. */
export const ENEMY_TYPES: readonly string[] = ['conscript', 'rifleman', 'marksman', 'heavy_gunner', 'at_team', 'tank', 'scout_drone', 'fpv_drone', 'attack_drone', 'shahed', 'attack_heli', 'car_bomb', 'kamikaze', 'cruise_missile', 'kalibr', 'ballistic', 'hgv'];
/** DEFCON 1's nuclear weapons, for the guide. */
export const NUCLEAR_TYPES: readonly string[] = ['icbm', 'hgv_nuclear'];
/** The fleet's ships, for the guide: never on the road, never in reach, only ever what they launch. */
export const SHIP_TYPES: readonly string[] = ['destroyer', 'drone_ship'];
/** The submarine of DEFCON 1. */
export const NUCLEAR_SHIP_TYPES: readonly string[] = ['submarine'];
